import { Injectable } from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';

interface CustomWindow extends Window {
  userFontSize?: 'small' | 'medium' | 'large';
}

// Make custom window properties accessible.
declare let window: CustomWindow;

@Injectable({
  providedIn: 'root',
})
export class TextSizeService {
  private readonly TEXT_SIZES = {
    small: 12,
    medium: 16,
    large: 20,
  };

  // Base text size in px. Default is 16.
  private _textSize = new BehaviorSubject<number>(16);
  textSize$ = this._textSize.asObservable();

  private readonly MIN_FONT_SIZE = 12;
  private readonly MAX_FONT_SIZE = 20;
  private readonly FONT_SIZE_STEP = 4;

  private readonly localStorageKey = 'app.baseTextSize';

  constructor() {
    this.initWatcher();

    const textSize = localStorage.getItem(this.localStorageKey);

    if (textSize !== null) {
      this.setTextSize(JSON.parse(textSize));
    }
  }

  /** Setup a timed monitor of window.userTheme to detect theme changes sent from mobile app. */
  initWatcher(): void {
    timer(0, 1000).subscribe(() => {
      if (window?.userFontSize) {
        if (
          window.userFontSize === 'small' &&
          this._textSize.getValue() !== this.TEXT_SIZES.small
        ) {
          this.setTextSize(this.TEXT_SIZES.small);
        } else if (
          window.userFontSize === 'medium' &&
          this._textSize.getValue() !== this.TEXT_SIZES.medium
        ) {
          this.setTextSize(this.TEXT_SIZES.medium);
        } else if (
          window.userFontSize === 'large' &&
          this._textSize.getValue() !== this.TEXT_SIZES.large
        ) {
          this.setTextSize(this.TEXT_SIZES.large);
        }
      }
    });
  }

  getTextSize(): number {
    return this._textSize.getValue();
  }

  setTextSize(textSize: number): void {
    this._textSize.next(textSize);
    localStorage.setItem(this.localStorageKey, JSON.stringify(textSize));
  }

  increaseTextSize(): void {
    if (this._textSize.getValue() < this.MAX_FONT_SIZE) {
      this.setTextSize(this.getTextSize() + this.FONT_SIZE_STEP);
    }
  }

  decreaseTextSize(): void {
    if (this._textSize.getValue() > this.MIN_FONT_SIZE) {
      this.setTextSize(this.getTextSize() - this.FONT_SIZE_STEP);
    }
  }
}
